import { scaleSqrt } from 'd3-scale';
import { packSiblings, type PackRadius } from 'd3-hierarchy';
import { niceRange } from '../model/util/coord';
import { getRange } from '../model/util/array';

interface BeeswarmDataItem {
  value: number;
  symbolSize: number;
}

function getAxisRange(data: number[]) {
  const [min, max] = getRange(data);
  return niceRange(min, max);
}

function getSymbolSizeScale(data: number[], symbolSizeRange: [number, number]) {
  const [dataMin, dataMax] = getRange(data);
  return scaleSqrt().domain([dataMin, dataMax]).range(symbolSizeRange);
}

function getSeriesWidth(data: BeeswarmDataItem[], axisRange: [number, number], splitNumber = 10) {
  const stepArr = [];
  const [min, max] = axisRange;
  const step = (max - min) / splitNumber;
  // 额外增加的空隙，确保有足够的空间
  const fixWidth = 20;
  for (let i = 0; i < splitNumber; i++) {
    const stepMin = min + i * step;
    const stepMax = min + (i + 1) * step;
    const stepData = data.filter(item => item.value >= stepMin && item.value < stepMax);
    stepArr.push(stepData);
  }

  const graphWidthPerStep = [];

  stepArr.forEach((stepData, index) => {
    if (stepData.length) {
      const stepDataRadius: PackRadius[] = stepData.map(item => ({ r: item.symbolSize / 2 }));
      packSiblings(stepDataRadius);
      graphWidthPerStep.push(getPackWidth(stepDataRadius as Required<PackRadius>[]));
    } else {
      graphWidthPerStep.push(0);
    }
  });
  // 单个step的宽度不具有参考性，因为实际宽度还会被相邻step积压，这里做格式化处理
  const influenceFactor = 0.5;
  const graphWidthPerStepFormat: number[] = [];

  graphWidthPerStep.forEach((width, index) => {
    const pre = graphWidthPerStep[index - 1] || 0;
    const next = graphWidthPerStep[index + 1] || 0;
    const formatWidth = width + pre * influenceFactor + next * influenceFactor;
    graphWidthPerStepFormat.push(formatWidth);
  });

  const maxGraphWidth = Math.max(...graphWidthPerStepFormat);

  // 取整为10的整数倍
  const maxGraphWidthRound = Math.floor((maxGraphWidth + fixWidth) / 10) * 10;

  return maxGraphWidthRound;
}

function getPackWidth(data: Required<PackRadius>[]) {
  let minX = Infinity;
  let maxX = -Infinity;
  data.forEach(d => {
    const { x, r } = d;
    if (x - r < minX) {
      minX = x - r;
    }
    if (x + r > maxX) {
      maxX = x + r;
    }
  });

  return maxX - minX;
}

export { getAxisRange, getSymbolSizeScale, getSeriesWidth };
